import React from 'react';
import { useParams } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import { parse, type CsvError } from 'csv-parse/browser/esm';
import uniqBy from 'lodash/uniqBy';
import Loader from '@/components/common/Loader';
import useBulkImports from '@/lib/hooks/swr/useBulkImports';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import { getFile, UploadContexts } from '@/modules/FileUploader';
import { showErrorMessage } from '@/utils/ToastFeedback';
import BulkImportStatus from '../../config/BulkImportStatus';
import useBulkImportCSV from '../../hooks/useBulkImportCSV';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import useUpdateBulkImport from '../../hooks/useUpdateBulkImport';
import { parseCsvHeaders } from '../../lib/utils/parseCsv';
import type BulkImportParams from '../../types/BulkImportParams';
import BackToUploadsButton from './BackToUploadsButton';
import CancelDataUploadButton from './CancelDataUploadButton';
import MatchHeadersBlockPrompt from './MatchHeadersBlockPrompt';
import { useMatchHeadersContext } from './MatchHeadersProvider';
import MatchHeadersTable from './MatchHeadersTable';
import PreviewCsvDialog from './PreviewCsvDialog';

function showGenericErrorToast() {
  showErrorMessage({
    message: translate('DATA_UPLOADS_FILE_UPLOAD_OTHER'),
  });
}

export default function MatchHeadersPage() {
  const isMountedRef = React.useRef(true);
  const { matchedHeaders } = useMatchHeadersContext();
  const updateBulkImport = useUpdateBulkImport();
  const ownerOrgId = useOwnerOrgId();
  const { bulkImportId } = useParams<BulkImportParams>();
  const { data: bulkImportData, error: bulkImportError } =
    useBulkImports(bulkImportId);
  const [isHidingEmptyData, setIsHidingEmptyData] = React.useState(false);
  const {
    headers = [],
    rows,
    setHeaders,
    setRows,
  } = useBulkImportCSV(isHidingEmptyData);
  const [canSubmit, setCanSubmit] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [totalMatchedHeaders, setTotalMatchedHeaders] = React.useState(0);
  const [canPreview, setCanPreview] = React.useState(false);

  React.useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  React.useEffect(() => {
    if (bulkImportError) {
      showGenericErrorToast();
    }

    if (bulkImportData) {
      (async () => {
        try {
          const { data: fileData } = await getFile({
            ownerOrgId,
            context: UploadContexts.IMPORTS,
            uploadId: btoa(decodeURIComponent(bulkImportData[0].inputS3Path)),
          });

          const csvText = await fileData.text();

          if (!isMountedRef.current) {
            return;
          }

          parse(
            csvText,
            {},
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (err: CsvError | undefined, csvData: any | undefined) => {
              if (!err) {
                try {
                  const headerRow = csvData.shift();
                  setHeaders(parseCsvHeaders(headerRow, csvData));
                  setRows(csvData);
                  setIsLoaded(true);
                } catch (e) {
                  showErrorMessage({
                    message: translate('DATA_UPLOADS_FILE_UPLOAD_OTHER'),
                  });
                  setIsLoaded(true);
                }
              }
            },
          );
        } catch (e) {
          if (!isMountedRef.current) {
            return;
          }

          showGenericErrorToast();
          setIsLoaded(true);
        }
      })();
    }
  }, [bulkImportData, bulkImportError]);

  React.useEffect(() => {
    if (!canSubmit && matchedHeaders.length) {
      setCanSubmit(true);
    } else if (canSubmit && !matchedHeaders.length) {
      setCanSubmit(true);
    }
  }, [canSubmit, matchedHeaders]);

  React.useEffect(() => {
    setTotalMatchedHeaders(
      uniqBy(matchedHeaders, (header) => header[1]).length,
    );
  }, [matchedHeaders]);

  const handleSwitchChange = () => setIsHidingEmptyData((prev) => !prev);
  const handleSubmit = React.useCallback(() => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const apiData = Object.fromEntries(
      matchedHeaders.map(([fieldName, csvHeaderIndex]) => [
        csvHeaderIndex.toString(),
        fieldName,
      ]),
    );

    updateBulkImport({
      bulkImportId,
      headerMappings: apiData,
      status: BulkImportStatus.HEADERS_MAPPED,
    }).catch(() => {
      setIsSubmitting(false);
    });
  }, [isSubmitting, matchedHeaders]);

  return (
    <>
      <div className="min-w-full p-4">
        <MatchHeadersBlockPrompt
          when={!isSubmitting && matchedHeaders.length > 0}
        />
        <Alert icon={false} severity="warning" className="md:hidden">
          {translate('DATA_UPLOADS_MOBILE_MESSAGE')}
        </Alert>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="type-h1">
            {translate('DATA_UPLOADS_NEW_DATA_UPLOAD')}
          </h2>
          <BackToUploadsButton />
        </div>
        <p className="mb-3 type-body1">
          {translate('DATA_UPLOADS_MATCH_HEADERS_INSTRUCTIONS_1')}
        </p>
        <p className="mb-3 type-body1">
          {translate('DATA_UPLOADS_MATCH_HEADERS_INSTRUCTIONS_2')}
        </p>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="mr-3 type-h2">
            {translate('DATA_UPLOADS_MATCH_HEADERS')}
          </h3>
          <span className="grow type-body1">
            {translate('DATA_UPLOADS_MATCH_HEADERS_TOTAL_MATCHED', {
              matched: totalMatchedHeaders,
              total: headers.length,
            })}
          </span>

          <Button onClick={() => setCanPreview(true)} variant="text">
            {translate('DATA_UPLOADS_PREVIEW')}
          </Button>

          <FormControlLabel
            label={translate('DATA_UPLOADS_HIDE_EMPTY_DATA')}
            labelPlacement="end"
            control={
              <Switch
                checked={isHidingEmptyData}
                onChange={handleSwitchChange}
              />
            }
          />
        </div>

        {!isLoaded ? (
          <Loader
            alignItems="center"
            justifyContent="center"
            display="flex"
            marginTop={4}
          />
        ) : (
          <>
            <MatchHeadersTable headers={headers} rows={rows} />
            <div className="mt-6 text-center">
              <Tooltip
                title={
                  canSubmit
                    ? ''
                    : translate(
                        'DATA_UPLOADS_MATCH_HEADERS_SUBMIT_DISABLED_MESSAGE',
                      )
                }
              >
                <span className="block">
                  <LoadingButton
                    disabled={!canSubmit}
                    loading={isSubmitting}
                    onClick={handleSubmit}
                    variant="contained"
                  >
                    {translate('DATA_UPLOADS_SUBMIT_FOR_REVIEW')}
                  </LoadingButton>
                </span>
              </Tooltip>
            </div>
            <div className="text-center">
              <CancelDataUploadButton
                bulkImportId={bulkImportId}
                disabled={isSubmitting}
              >
                {translate('DATA_UPLOADS_CANCEL_UPLOAD')}
              </CancelDataUploadButton>
            </div>
          </>
        )}
      </div>

      <PreviewCsvDialog
        open={canPreview}
        onClose={() => setCanPreview(false)}
        rows={rows || []}
        headers={headers}
      />
    </>
  );
}
